import { FormEventHandler, useMemo, useState } from 'react';
import { StyledAccordionItem, StyledFacetHeaderCount, StyledFacetSelection, StyledFacetsOverlayFormAccordion } from '~/features/facets/components/FacetsOverlay/components/FacetsOverlayForm/styled';
import { FacetsOverlayHeader } from '~/features/facets/components/FacetsOverlay/components/FacetsOverlayHeader/FacetsOverlayHeader';
import { FacetsOverlayTitle } from '~/features/facets/components/FacetsOverlay/components/FacetsOverlayTitle/FacetsOverlayTitle';
import ChevronDown from '~/icons/chevron-down.svg';
import ChevronUp from '~/icons/chevron-up.svg';
import { IFacet, IFacetValue } from '~/lib/data-contract';
import { AccordionItemProps } from '~/shared/components/Accordion/components/AccordionItem/AccordionItem';
import { Button } from '~/shared/components/Button/Button';
import { ButtonGroup } from '~/shared/components/ButtonGroup/ButtonGroup';
import { DialogFooter } from '~/shared/components/Dialog/components/DialogFooter/DialogFooter';
import { DialogForm } from '~/shared/components/Dialog/components/DialogForm/DialogForm';
import { Headline } from '~/shared/components/Headline/Headline';
import { Icon } from '~/shared/components/Icon/Icon';
import { useTranslation } from '~/shared/utils/translation/hooks/useTranslation';

const isIdentical = <T extends unknown>(first: T, second: T): boolean => JSON.stringify(first) === JSON.stringify(second);

export type FacetsOverlayFormProps = {
	facets: IFacet[];
	onFacetValueToggle: (facetId: string, facetValueId: string) => Promise<IFacet[]>;
	onFacetsClear: () => Promise<IFacet[] | void>;
	onSubmit: () => void;
};

export const FacetsOverlayForm = ({ facets, onFacetValueToggle, onFacetsClear, onSubmit }: FacetsOverlayFormProps) => {
	const { translate } = useTranslation();
	const [optionIsFetching, setOptionIsFetching] = useState<string | undefined>(undefined);

	const [appliedFacets, setAppliedFacets] = useState<IFacet[]>(facets);

	const handleFacetValueSelected = (facetId: string) => async (optionId: string) => {
		if (optionIsFetching) {
			return;
		}

		setOptionIsFetching(optionId);

		const newFacets = await onFacetValueToggle(facetId, optionId);
		setAppliedFacets(newFacets);

		setOptionIsFetching(undefined);
	};

	const hasChanges: boolean = useMemo(() => {
		return !isIdentical<IFacet[]>(appliedFacets, facets);
	}, [appliedFacets]);

	const handleSubmit: FormEventHandler = (event) => {
		event.preventDefault();

		if (hasChanges) {
			onSubmit();
		}
	};

	const hasAppliedFacetsSelected = useMemo(() => {
		let hasSelected = false;

		for (const facet of appliedFacets) {
			for (const facetValue of facet.values) {
				if (facetValue.selected) {
					hasSelected = true;
					break;
				}
			}

			if (hasSelected) {
				break;
			}
		}

		return hasSelected;
	}, [appliedFacets]);

	const handleClearAll = async () => {
		if (!hasAppliedFacetsSelected || optionIsFetching) {
			return;
		}

		setOptionIsFetching('clear-all');

		const newFacets = await onFacetsClear();
		if (newFacets) {
			setAppliedFacets(newFacets);
		}

		setOptionIsFetching(undefined);
	};

	const accordionItemProps: Partial<AccordionItemProps> = {
		icon: ({ isOpen }) => <Icon>{isOpen ? <ChevronUp /> : <ChevronDown />}</Icon>,
	};

	const getValuesCount = (values: IFacetValue[]) => {
		const selectedValues = values.map((facetValue) => facetValue.selected);
		const selectedValuesLength = selectedValues.filter(Boolean).length;

		if (selectedValuesLength > 0) {
			return selectedValuesLength;
		}

		return '';
	};

	const getItemHeader = (item: IFacet) => {
		const valuesCount = getValuesCount(item.values);
		if (valuesCount) {
			return (
				<Headline size={5}>
					{item.label}
					<StyledFacetHeaderCount> ({valuesCount})</StyledFacetHeaderCount>
				</Headline>
			);
		}

		return item.label;
	};

	return (
		<DialogForm onSubmit={handleSubmit}>
			<FacetsOverlayHeader>
				<FacetsOverlayTitle>{translate('filter.filter-button-label')}</FacetsOverlayTitle>
			</FacetsOverlayHeader>

			<StyledFacetsOverlayFormAccordion>
				{appliedFacets.map((item) => (
					<StyledAccordionItem
						key={`facets_accordion_item_${item.id}`}
						header={getItemHeader(item)}
						{...accordionItemProps}
					>
						<StyledFacetSelection
							facetValues={item.values}
							onFacetValueSelected={handleFacetValueSelected(item.id)}
							facetLoading={optionIsFetching}
						/>
					</StyledAccordionItem>
				))}
			</StyledFacetsOverlayFormAccordion>

			<DialogFooter>
				<ButtonGroup>
					<Button
						variant="outline"
						disabled={!hasAppliedFacetsSelected}
						onClick={handleClearAll}
					>
						{translate('filter.clear-all')}
					</Button>

					<Button
						type="submit"
						disabled={!hasChanges}
					>
						{translate('filter.apply')}
					</Button>
				</ButtonGroup>
			</DialogFooter>
		</DialogForm>
	);
};
